import { onMounted, watch } from "vue";
import { storeToRefs } from "pinia";
import { useDebounceFn } from "@vueuse/core";

import { useMCampaignTargetsStore } from "@stores/mCampaignTargets";
import { createFilters } from "@/utils/composables/filters/createFilters";
import { useFiltersUrlSync, arrayParam, type UrlFiltersState } from "@/utils/composables/filters/useFiltersUrlSync";

// The search box stays out of the URL: this store drives its own debounced
// search from a watcher, so restoring the query would fetch twice.
const urlParams = {
  status: arrayParam(),
};

type TargetsUrlState = UrlFiltersState<typeof urlParams>;

export const useTargetsAndQuotasFilters = () => {
  const mCampaignTargetsStore = useMCampaignTargetsStore();

  const { statusFilter } = storeToRefs(mCampaignTargetsStore);
  const { search, clearFilters } = mCampaignTargetsStore;

  const filters = createFilters({
    filters: [
      {
        id: "status",
        label: "Status",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        model: statusFilter,
      },
    ],
    search,
    clear: clearFilters,
  });

  const applyUrlState = (state: TargetsUrlState) => {
    statusFilter.value = state.status;
  };

  const { syncUrl, restoreFromUrl } = useFiltersUrlSync({
    params: urlParams,
    serialize: () => ({ status: statusFilter.value }),
    apply: applyUrlState,
    onExternalChange: async (state) => {
      applyUrlState(state);
      await search();
    },
  });

  const restored = restoreFromUrl();

  onMounted(async () => {
    if (restored) await search();
  });

  watch(statusFilter, useDebounceFn(syncUrl, 300), { deep: true });

  return filters;
};
